import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  createOutOfOffice,
  outOfOfficeFormQuery,
  outOfOfficeRecordQuery,
  outOfOfficeQuery,
  saveOutOfOffice,
} from '../../api/settingsMyAccount'
import type { Choice } from '../../api/settingsUsers'
import { exact, fromDuration, fromNow, stamp } from '../../inventoree/format'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { CheckField, FormActions, FormRow, HalfField } from '../../ui/FormGrid'
import { Forbidden } from '../../ui/Forbidden'
import { Modal } from '../../ui/Modal'
import { Spinner } from '../../ui/Spinner'
import { toast } from '../../ui/notify'
import { esc, failed, tableRequest } from '../users/shared'

/**
 * Out Of Office - `user_settings/out_of_office/index.php` (lista + modala),
 * `new_record.php`, `out_of_office/tab_list.php` (randul desfasurat, cu o
 * singura fila) si `edit_form.php`.
 *
 * Coloanele sunt cele din `OutOfOfficeModel` (`public/js/settings/user_accounts.js`),
 * cu acelasi steag verde/rosu la capat: verde cat timp perioada nu s-a incheiat.
 *
 * O perioada deja incheiata nu se mai schimba - macheta veche arata in locul
 * formularului un rand galben, "You can not make other changes", si asa e si
 * aici. Ce s-a schimbat: capatul spune de ce refuza, in loc sa nu faca nimic.
 */

const ORDER_NAMES = ['ID', 'UserID', 'Substitute', 'From', 'To', 'From', '']

/** `moment(x).format("MMM Do YYYY, HH:mm")` din `OutOfOfficeModel`. */
function stampText(value: unknown): string {
  const when = stamp(value)
  if (!when) return ''
  /* `exact()` e "MMM Do YYYY, HH:mm:ss"; modelul vechi n-are secunde. */
  return exact(when).replace(/:\d{2}$/, '')
}

function oofColumns(): TableColumn[] {
  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 50,
      html: (row) =>
        `<center class="show_details color-dark-grey pointer font-16" data-id="${Number(row.ID)}"><i class="fas fa-plus-circle"></i></center>`,
    },
    {
      index: 1,
      title: 'User',
      orderable: true,
      visible: true,
      html: (row) => `<i class="fa fa-user color-red"></i> ${esc(row.user)}`,
    },
    {
      index: 2,
      title: 'Substitute',
      orderable: true,
      visible: true,
      html: (row) => `<i class="fa fa-user color-green"></i> ${esc(row.substitute)}`,
    },
    { index: 3, title: 'Start date', orderable: true, visible: true, html: (row) => esc(stampText(row.from)) },
    { index: 4, title: 'End date', orderable: true, visible: true, html: (row) => esc(stampText(row.to)) },
    {
      index: 5,
      title: 'End',
      orderable: true,
      visible: true,
      html: (row) => {
        const when = stamp(row.to)
        if (!when) return ''
        /* `moment().to(x)` cat timp mai e vreme, `moment(x).fromNow()` dupa. */
        const now = new Date()
        return esc(when > now ? 'in ' + fromDuration(when, now) : fromNow(when, now))
      },
    },
    {
      index: 6,
      title: 'Out of office',
      orderable: false,
      visible: true,
      width: 100,
      html: (row) => {
        const when = stamp(row.to)
        const live = when !== null && when > new Date()
        return `<center class="${live ? 'color-green2' : 'color-red'}"><i class="fa fa-flag"></i></center>`
      },
    },
  ]
}

export function OutOfOfficePanel() {
  const client = useQueryClient()
  const [adding, setAdding] = useState(false)
  const [state, setState] = useTableState('settings-my-account-oof', {
    start: 0,
    length: 25,
    /* `order: [[3, 'desc']]` din `OutOfOffice.initDatatable`. */
    order: { column: 3, dir: 'desc' },
    search: '',
  })
  const columns = useMemo(() => oofColumns(), [])
  const table = useQuery({
    ...outOfOfficeQuery(tableRequest(state, ORDER_NAMES, 'From')),
    placeholderData: keepPreviousData,
  })

  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />

  const reload = () => void client.invalidateQueries({ queryKey: ['settings', 'my_account', 'oof'] })

  return (
    <div id="content">
      <button type="button" className="pull-right btn btn-success btn-sm" onClick={() => setAdding(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> New record
      </button>
      <div className="clearfix"></div>
      <DataTable
        id="settings-my-account-oof"
        columns={columns}
        rows={(table.data?.data ?? []) as unknown as Record<string, unknown>[]}
        total={table.data?.recordsFiltered ?? 0}
        fetching={table.isFetching}
        error={table.error ? failed(table.error) : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => `row_${Number(row.ID)}`}
        emptyHint="Tell your colleagues when you are away and who takes over."
        details={(row) => <OofWorkspace recordId={Number(row.ID)} onSaved={reload} />}
      />

      <NewOofModal
        open={adding}
        onClose={() => setAdding(false)}
        onSaved={() => {
          setAdding(false)
          reload()
        }}
      />
    </div>
  )
}

/** `out_of_office/tab_list.php`: o singura fila, "Edit". */
function OofWorkspace({ recordId, onSaved }: { recordId: number; onSaved(): void }) {
  const page = useQuery(outOfOfficeRecordQuery(recordId))

  if (page.error) return <div className="alert alert-danger">{failed(page.error)}</div>
  if (!page.data) return <Spinner />

  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          <li>
            <a
              href={`/app/settings/user_accounts/out_of_office#${recordId}`}
              className="action_button active"
              onClick={(e) => e.preventDefault()}
            >
              Edit
            </a>
          </li>
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {page.data.closed ? (
            <div className="alert alert-warning">
              <i className="fas fa-info-circle" aria-hidden="true"></i> You can not make other changes.
            </div>
          ) : (
            <EditOofForm recordId={recordId} record={page.data.record} admins={page.data.admins} onSaved={onSaved} />
          )}
        </div>
      </div>
    </div>
  )
}

/** `datetime-local` cere `YYYY-MM-DDTHH:mm`; baza da `YYYY-MM-DD HH:mm:ss`. */
function forInput(value: string): string {
  const when = stamp(value)
  if (!when) return ''
  const two = (n: number) => String(n).padStart(2, '0')
  return `${when.getFullYear()}-${two(when.getMonth() + 1)}-${two(when.getDate())}T${two(when.getHours())}:${two(when.getMinutes())}`
}

function EditOofForm({
  recordId,
  record,
  admins,
  onSaved,
}: {
  recordId: number
  record: { from: string; to: string; details: string; myTickets: boolean; myCompanies: boolean; substitute: number }
  admins: Choice[]
  onSaved(): void
}) {
  const [form, setForm] = useState({
    substitute: record.substitute > 0 ? String(record.substitute) : '',
    start: forInput(record.from),
    end: forInput(record.to),
    details: record.details,
    seeCompanies: record.myCompanies,
    takeTickets: record.myTickets,
  })
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    setForm({
      substitute: record.substitute > 0 ? String(record.substitute) : '',
      start: forInput(record.from),
      end: forInput(record.to),
      details: record.details,
      seeCompanies: record.myCompanies,
      takeTickets: record.myTickets,
    })
  }, [record])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    void saveOutOfOffice(recordId, {
      substitute: form.substitute,
      start_date: form.start.replace('T', ' '),
      end_date: form.end.replace('T', ' '),
      details: form.details,
      see_companies: form.seeCompanies ? 'on' : '',
      take_tickets: form.takeTickets ? 'on' : '',
    })
      .then(() => {
        toast('Record updated', 'Success')
        onSaved()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <div className="panel-body">
      <form method="post" className="editOofForm form-horizontal" onSubmit={submit}>
        <FormRow>
          <HalfField label="Substitute" required htmlFor={`oof_substitute_${recordId}`}>
            <select
              id={`oof_substitute_${recordId}`}
              className="form-control"
              required
              value={form.substitute}
              onChange={(e) => setForm({ ...form, substitute: e.target.value })}
            >
              <option value="">Select...</option>
              {admins.map((admin) => (
                <option key={admin.id} value={admin.id}>
                  {admin.name}
                </option>
              ))}
            </select>
          </HalfField>
        </FormRow>

        {/* `style={{ margin: '15px -30px' }}` a plecat. Peste cei -15px pe care
            `form-horizontal` ii da oricum fiecarui `form-group`, mai adauga 15,
            deci formularul intreg atarna cu 15px in afara marginii panoului
            (masurat: campurile incepeau la 5px de marginea lui, nu la 20px).
            Fara el, toate randurile incep si se termina exact pe marginile
            panoului, ca la fisa contului. */}
        <FormRow>
          <HalfField label="Start date" required htmlFor={`oof_start_${recordId}`}>
            <input
              id={`oof_start_${recordId}`}
              className="form-control"
              type="datetime-local"
              required
              value={form.start}
              onChange={(e) => setForm({ ...form, start: e.target.value })}
            />
          </HalfField>
          <HalfField label="End date" required htmlFor={`oof_end_${recordId}`}>
            <input
              id={`oof_end_${recordId}`}
              className="form-control"
              type="datetime-local"
              required
              value={form.end}
              onChange={(e) => setForm({ ...form, end: e.target.value })}
            />
          </HalfField>
        </FormRow>

        <FormRow>
          <CheckField
            id={`oof_see_companies_${recordId}`}
            label="See my companies"
            checked={form.seeCompanies}
            onChange={(on) => setForm({ ...form, seeCompanies: on })}
          />
          <CheckField
            id={`oof_take_tickets_${recordId}`}
            label="Take my tickets"
            checked={form.takeTickets}
            onChange={(on) => setForm({ ...form, takeTickets: on })}
          />
        </FormRow>

        {/* Details ramane de doua randuri: e o nota scrisa de om, nu un camp de
            o linie. Marginea din stanga si latimea sunt ale grilei, ca la restul. */}
        <FormRow>
          <HalfField label="Details" htmlFor={`oof_details_${recordId}`}>
            <textarea
              id={`oof_details_${recordId}`}
              className="form-control"
              rows={2}
              value={form.details}
              onChange={(e) => setForm({ ...form, details: e.target.value })}
            />
          </HalfField>
        </FormRow>

        <FormActions>
          <button type="submit" className="btn btn-danger goEditOof" disabled={busy}>
            <i className="fa fa-check"></i> {busy ? 'Saving...' : 'Update'}
          </button>
        </FormActions>
      </form>
    </div>
  )
}

/** `out_of_office/new_record.php`. */
function NewOofModal({ open, onClose, onSaved }: { open: boolean; onClose(): void; onSaved(): void }) {
  const choices = useQuery({ ...outOfOfficeFormQuery, enabled: open })
  const [form, setForm] = useState({
    substitute: '',
    start: '',
    end: '',
    details: '',
    seeCompanies: false,
    takeTickets: false,
    departments: [] as number[],
    informCustomers: false,
    message: '',
  })
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open || !choices.data) return
    setForm((prev) => (prev.message === '' ? { ...prev, message: choices.data.message } : prev))
  }, [open, choices.data])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    void createOutOfOffice({
      substitute: form.substitute,
      start_date: form.start.replace('T', ' '),
      end_date: form.end.replace('T', ' '),
      details: form.details,
      see_companies: form.seeCompanies ? 'on' : '',
      take_tickets: form.takeTickets ? 'on' : '',
      'inform-departments': form.departments,
      'inform-customers': form.informCustomers ? 'on' : '',
      'inform-message': form.message,
    })
      .then((answer) => {
        /*
         * Se spune CATE instiintari au plecat. Portalul trimitea in bucla si nu
         * se uita la nimic, deci un server de mail picat trecea in tacere si
         * omul credea ca si-a anuntat colegii.
         */
        const asked = form.departments.length > 0 || form.informCustomers
        toast(
          asked
            ? `Record saved. ${answer.notified} notification${answer.notified === 1 ? '' : 's'} sent.`
            : 'Record saved.',
          'Success',
        )
        setForm({
          substitute: '',
          start: '',
          end: '',
          details: '',
          seeCompanies: false,
          takeTickets: false,
          departments: [],
          informCustomers: false,
          message: choices.data?.message ?? '',
        })
        onSaved()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <Modal id="new-oof-modal" open={open} onClose={onClose} title="New record" labelledBy="newOofModalLabel">
      <form method="post" id="newOofForm" onSubmit={submit}>
        <div className="form-group">
          <label>Substitute</label>
          <select
            className="form-control"
            required
            value={form.substitute}
            onChange={(e) => setForm({ ...form, substitute: e.target.value })}
          >
            <option value="">Select...</option>
            {(choices.data?.admins ?? []).map((admin) => (
              <option key={admin.id} value={admin.id}>
                {admin.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group row">
          <div className="col-md-6">
            <label>Start date</label>
            <input
              className="form-control"
              type="datetime-local"
              required
              value={form.start}
              onChange={(e) => setForm({ ...form, start: e.target.value })}
            />
          </div>
          <div className="col-md-6">
            <label>End date</label>
            <input
              className="form-control"
              type="datetime-local"
              required
              value={form.end}
              onChange={(e) => setForm({ ...form, end: e.target.value })}
            />
          </div>
        </div>

        {/* Bifele sunt patratelele obisnuite, cu eticheta la dreapta lor -
            aceleasi ca in restul ecranului. Latimile campurilor raman ale
            ferestrei: o modala de 570px e deja o singura coloana, si o casuta
            de mesaj taiata la jumatatea ei n-ar mai fi de scris in. */}
        <FormRow>
          <CheckField
            id="new_oof_see_companies"
            label="See my companies"
            checked={form.seeCompanies}
            onChange={(on) => setForm({ ...form, seeCompanies: on })}
          />
          <CheckField
            id="new_oof_take_tickets"
            label="Take my tickets"
            checked={form.takeTickets}
            onChange={(on) => setForm({ ...form, takeTickets: on })}
          />
        </FormRow>

        <div className="form-group">
          <label>Details</label>
          <textarea
            className="form-control"
            rows={2}
            value={form.details}
            onChange={(e) => setForm({ ...form, details: e.target.value })}
          />
        </div>

        <label className="color">
          <strong>Send</strong> notification
        </label>

        <div className="form-group">
          <label>Departments</label>
          <select
            className="form-control"
            multiple
            size={6}
            value={form.departments.map(String)}
            onChange={(event) =>
              setForm({
                ...form,
                departments: Array.from(event.currentTarget.selectedOptions).map((o) => Number(o.value)),
              })
            }
          >
            {(choices.data?.departments ?? []).map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        </div>

        <FormRow>
          <CheckField
            id="new_oof_inform_customers"
            label="Inform customers"
            checked={form.informCustomers}
            onChange={(on) => setForm({ ...form, informCustomers: on })}
          />
        </FormRow>

        <div className="form-group">
          <label>Message</label>
          <textarea
            className="form-control"
            rows={10}
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
          />
          <span className="help-block">
            <code>[period]</code>, <code>[substitute]</code> and <code>[contact]</code> are filled in when the message
            goes out.
          </span>
        </div>

        <div className="form-group aright">
          <button type="button" className="btn btn-default" data-dismiss="modal" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className="btn btn-success goSaveOof" disabled={busy}>
            <i className="fa fa-check"></i> {busy ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
